import { AfterContentInit, Component } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { Router } from '@angular/router';

import { take } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { CookieService } from 'ngx-cookie-service';

import { MessageService } from '@core/service/message.service';
import { RedirectionService } from '@shared/services/redirection.service';
import { ProductValidationsService } from './product-validations.service';
import { SimulatorService } from '@core/service/simulator.service';

import { environment } from '@environments/environment';

import {
  MENSAGE_COUNTER_OFFER_ACTIVE,
  MENSSAGE_PENDING_SIGNATURE,
  MESSAGE_ALGO_SALIO_MAL,
  show_popup,
  TITLE_ERROR,
  TITLE_MENSAJE,
} from '@shared/utility/popup-error';

import { NextFlow } from '@core/model/enums/next-flow';
import { CustomResponseDto } from '@core/model/custom-response-dto';
import { InitialProductValidationResponse } from '@model/InitialProductValidationResponse';

// Shared
import { ROUTES_CONSTANTS } from '@shared/configs';

const DISPERSION_METHOD_STEPS_CONSTANT = [
  'STEP1',
  'STEP1.1',
  'STEP1.2',
  'STEP1.3',
  'STEP2',
  'STEP2.1',
  'STEP2.2',
  'STEP2.3',
  'account/references',
  'references',
];

@Component({
  selector: 'app-product-validations',
  templateUrl: './product-validations.component.html',
  styleUrls: ['./product-validations.component.css'],
})
export class ProductValidationsComponent implements AfterContentInit {
  public showLoader: boolean;
  public data: CustomResponseDto<InitialProductValidationResponse>;

  private readonly queue: string;
  private readonly domain: string;

  constructor(
    private readonly redirect: RedirectionService,
    private readonly productValidationService: ProductValidationsService,
    private readonly messageService: MessageService,
    private readonly currencyPipe: CurrencyPipe,
    private readonly cookieService: CookieService,
    private readonly simulatorService: SimulatorService,
    private readonly router: Router
  ) {
    this.showLoader = false;
    this.queue = 'product-validations';
    this.domain = environment.domain;
  }

  public ngAfterContentInit(): void {
    this.startWorkflow();
  }

  public startWorkflow(): void {
    this.showLoader = true;
    this.messageService.subscribe(this.queue).subscribe(
      (message) => {
        this.data = message;
        if (!this.data) {
          this.productValidationService.startWorkflow().subscribe(
            (response: CustomResponseDto<InitialProductValidationResponse>) => {
              this.showLoader = false;
              if (DISPERSION_METHOD_STEPS_CONSTANT.includes(response.response.navigableResponseDto.urlRedirect)) {
                this.router.navigate([ROUTES_CONSTANTS.DISPERSION_METHOD]);
                return;
              }

              if (response.response.navigableResponseDto.nextFlow === NextFlow.DispersionMethodSelection) {
                this.router.navigate([ROUTES_CONSTANTS.DECISION_MODEL]);
                return;
              }

              if (
                response.response.navigableResponseDto.message != null &&
                response.response.navigableResponseDto.nextFlow == NextFlow.FinalDecisionFlow
              ) {
                show_popup(TITLE_MENSAJE, response.response.navigableResponseDto.message).then(() => {
                  if (response.response.navigableResponseDto.urlRedirect.startsWith('http')) {
                    window.location.href = response.response.navigableResponseDto.urlRedirect;
                  } else {
                    this.redirect.navigateTo(response.response.navigableResponseDto.urlRedirect);
                  }
                });
              } else if (response.response.navigableResponseDto.nextFlow == NextFlow.DataValidationFlow) {
                this.redirect.navigateTo(response.response.navigableResponseDto.urlRedirect);
              } else if (
                sessionStorage.getItem('ac') != null &&
                response.response.navigableResponseDto.urlRedirect == '/signLoanApplication'
              ) {
                this.informResumeLoanApplication(
                  MENSSAGE_PENDING_SIGNATURE,
                  response.response.navigableResponseDto.urlRedirect
                );
              } else if (
                sessionStorage.getItem('ac') != null &&
                response.response.navigableResponseDto.urlRedirect == 'account/counterOffer'
              ) {
                this.informResumeLoanApplication(
                  MENSAGE_COUNTER_OFFER_ACTIVE,
                  response.response.navigableResponseDto.urlRedirect
                );
              } else {
                this.resumeRequestPopUp(
                  response.response.amount,
                  response.response.term,
                  response.response.navigableResponseDto.urlRedirect
                );
              }
            },
            () => {
              this.showLoader = false;
              show_popup(TITLE_ERROR, MESSAGE_ALGO_SALIO_MAL);
            }
          );
        }
      },
      () => {
        this.showLoader = false;
        show_popup(TITLE_ERROR, MESSAGE_ALGO_SALIO_MAL);
      }
    );
  }

  resumeRequestPopUp(amount: number, term: number, urlRedirect: string) {
    if (amount != null && term != null) {
      this.simulatorService
        .quotasValue({ amount, term })
        .pipe(take(1))
        .subscribe({
          next: (quota) => {
            this.setCookiesAmountAndTerm(amount, term, quota);
          },
        });
      Swal.fire({
        title: 'Mensaje',
        text: `Identificamos que ya tienes una solicitud en proceso por un crédito de ${this.currencyPipe.transform(
          amount,
          'COP',
          'symbol',
          '1.0-0'
        )} a ${term} cuotas, por lo cual vamos a continuar con los pasos faltantes`,
        type: null,
        onClose: () => {
          this.redirect.navigateTo(urlRedirect);
        },
        confirmButtonColor: '#4148F5',
        keydownListenerCapture: true,
      });
    } else if (urlRedirect) {
      if (urlRedirect.startsWith('http')) {
        window.location.href = urlRedirect;
      } else {
        this.redirect.navigateTo(urlRedirect);
      }
    } else {
      show_popup(TITLE_ERROR, MESSAGE_ALGO_SALIO_MAL);
    }
  }
  informResumeLoanApplication(body: string, urlRedirect: string) {
    Swal.fire({
      title: 'Importante',
      text: body,
      type: null,
      onClose: () => {
        this.redirect.navigateTo(urlRedirect);
      },
      confirmButtonColor: '#4148F5',
      keydownListenerCapture: true,
    });
  }

  setCookiesAmountAndTerm(amount: number, term: number, quota: number) {
    this.cookieService.delete('amount');
    this.cookieService.delete('term');
    this.cookieService.delete('quota');

    this.cookieService.set('amount', JSON.stringify(amount), 1, '/', this.domain);
    this.cookieService.set('term', JSON.stringify(term), 1, '/', this.domain);
    this.cookieService.set('quota', JSON.stringify(quota), 1, '/', this.domain);
  }
}
